import React from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { Tag } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { ISODateTimeString } from 'src/utils/date';
import { CalendarEvent } from 'src/utils/calendar';

import { LiveTrainingCardDesktop } from './LiveTrainingCardDesktop';
import { LiveTrainingCardMobile } from './LiveTrainingCardMobile';

export type TrainingStatus = 'upcoming' | 'completed';

// Calendar action labels
export const CALENDAR_LABELS = {
  GOOGLE: 'Add to Google calendar',
  YAHOO: 'Add to Yahoo calendar',
  ICS: 'Download .ics',
} as const;

// Menu item labels
export const MENU_LABELS = {
  SAVE: 'Save',
  UNSAVE: 'Unsave',
  DOWNLOAD_MATERIALS: 'Download course materials',
} as const;

// Button labels
export const BUTTON_LABELS = {
  SAVE: 'Save',
  ADD_TO_CALENDAR: 'Add to calendar',
  COURSE_MATERIALS: 'Course materials',
  COMPLETION_CERTIFICATE: 'Completion certificate',
  DOWNLOAD_COMPLETION_CERTIFICATE: 'Download completion certificate',
  JOIN_VIDEO_CALL: 'Join via video call',
} as const;

// Tag labels
export const TAG_LABELS = {
  COMPLETE: 'Complete',
  ENROLLED: 'Enrolled',
  FREE: 'Free',
} as const;

// Tag border colors
export const TAG_BORDER_COLORS = {
  COMPLETE: token('color.background.accent.lime.subtle'),
  ENROLLED: '#8FB8F6',
} as const;

// Tag background colors
export const TAG_BACKGROUND_COLORS = {
  ENROLLED: '#8FB8F6',
} as const;

// Training status values
export const TRAINING_STATUS = {
  UPCOMING: 'upcoming' as TrainingStatus,
  COMPLETED: 'completed' as TrainingStatus,
} as const;

// Text labels
export const TEXT_LABELS = {
  PAST: 'Past',
} as const;

const getCalendarTypeFromLabel = (label: string): CalendarEvent['calendarType'] => {
  switch (label) {
    case CALENDAR_LABELS.GOOGLE:
      return 'google';
    case CALENDAR_LABELS.YAHOO:
      return 'yahoo';
    case CALENDAR_LABELS.ICS:
      return 'ics';
    default:
      return undefined;
  }
};

export interface LiveTrainingCardProps {
  title: string;
  subtitle: string;
  startDateTime: ISODateTimeString;
  endDateTime: ISODateTimeString;
  status: TrainingStatus;
  enrolled: boolean;
  products: string[];
  host: string;
  isFree: boolean;
  isSaved: boolean;
  isPrivate?: boolean;
  isCard?: boolean;
  onSave?: () => void;
  onDetailsClick?: () => void;
  onAddToCalendar?: (calendarEvent: CalendarEvent) => void;
  onDownloadMaterials?: () => void;
  onDownloadCompletionCertificate?: () => void;
  showTimeUntilStart?: boolean;
  showAddToCalendar?: boolean;
  showDownloadMaterials?: boolean;
  showDownloadCompletionCertificate?: boolean;
  isCompletionCertificateLoading?: boolean;
  videoCallUrl?: string;
  hideButtonRow?: boolean;
  hideJoinVideoCallOnMobile?: boolean;
}

export const LiveTrainingCard = ({
  title,
  subtitle,
  startDateTime,
  endDateTime,
  status,
  enrolled,
  products = [],
  host,
  isFree,
  isSaved,
  isCard = true,
  onSave,
  onDetailsClick,
  onAddToCalendar,
  onDownloadMaterials,
  onDownloadCompletionCertificate,
  showTimeUntilStart = true,
  showAddToCalendar = false,
  showDownloadMaterials = false,
  videoCallUrl,
  showDownloadCompletionCertificate = false,
  isCompletionCertificateLoading = false,
  hideButtonRow = false,
  hideJoinVideoCallOnMobile = false,
}: LiveTrainingCardProps) => {
  const isCompleted = status === TRAINING_STATUS.COMPLETED;
  const isInPast = new Date(endDateTime) < new Date();

  // Business logic handlers
  const handleAddToCalendar = (label: string): void => {
    const calendarEvent: CalendarEvent = {
      title: title,
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      description: subtitle,
      calendarType: getCalendarTypeFromLabel(label),
      videoCallUrl: videoCallUrl,
    };

    if (onAddToCalendar) {
      onAddToCalendar(calendarEvent);
    }
  };

  const handleDownloadMaterials = (): void => {
    if (onDownloadMaterials) {
      onDownloadMaterials();
    }
  };

  const handleDownloadCompletionCertificate = (): void => {
    if (onDownloadCompletionCertificate) {
      onDownloadCompletionCertificate();
    }
  };

  const handleJoinViaVideoCall = (): void => {
    if (videoCallUrl) {
      window.open(videoCallUrl, '_blank');
    }
  };

  const handleSave = (): void => {
    if (onSave) {
      onSave();
    }
  };

  // Generate tags
  const getTags = (): React.ReactNode[] => {
    const tags: React.ReactNode[] = [];

    if (isCompleted) {
      tags.push(<Tag key="completed" label={TAG_LABELS.COMPLETE} isTall borderColor={TAG_BORDER_COLORS.COMPLETE} />);
    }

    if (status === TRAINING_STATUS.UPCOMING && enrolled) {
      tags.push(
        <Tag key="enrolled" label={TAG_LABELS.ENROLLED} isTall backgroundColor={TAG_BACKGROUND_COLORS.ENROLLED} />,
      );
    }

    if (isFree) {
      tags.push(<Tag key="free" label={TAG_LABELS.FREE} isTall />);
    }

    return tags;
  };

  // Build meatball menu items for mobile
  const getMeatballMenuItems = () => {
    const items: Array<{ label: string; onClick: () => void }> = [];

    if (onSave) {
      items.push({
        label: isSaved ? MENU_LABELS.UNSAVE : MENU_LABELS.SAVE,
        onClick: handleSave,
      });
    }

    if (showDownloadMaterials && onDownloadMaterials) {
      items.push({
        label: MENU_LABELS.DOWNLOAD_MATERIALS,
        onClick: handleDownloadMaterials,
      });
    }

    if (showAddToCalendar && !isInPast && onAddToCalendar) {
      Object.values(CALENDAR_LABELS).forEach((label) => {
        items.push({
          label,
          onClick: () => handleAddToCalendar(label),
        });
      });
    }

    return items;
  };

  const tags = getTags();
  const meatballMenuItems = getMeatballMenuItems();

  // Shared props for both components
  const sharedProps = {
    title,
    startDateTime,
    endDateTime,
    isInPast,
    isCompleted,
    enrolled,
    host,
    isFree,
    isSaved,
    videoCallUrl,
    isCompletionCertificateLoading,
    showDownloadCompletionCertificate,
    isCard,
    onDetailsClick,
    onSave: handleSave,
    onDownloadCompletionCertificate: handleDownloadCompletionCertificate,
    onJoinViaVideoCall: hideJoinVideoCallOnMobile ? undefined : handleJoinViaVideoCall,
    tags,
  };

  return (
    <>
      {/* Desktop version */}
      <div css={desktopOnlyStyles}>
        <LiveTrainingCardDesktop
          {...sharedProps}
          subtitle={subtitle}
          products={products}
          showAddToCalendar={showAddToCalendar}
          showDownloadMaterials={showDownloadMaterials}
          showTimeUntilStart={showTimeUntilStart}
          hideButtonRow={hideButtonRow}
          onAddToCalendar={handleAddToCalendar}
          onDownloadMaterials={handleDownloadMaterials}
        />
      </div>

      {/* Mobile version */}
      <div css={mobileOnlyStyles}>
        <LiveTrainingCardMobile {...sharedProps} meatballMenuItems={meatballMenuItems} />
      </div>
    </>
  );
};

const desktopOnlyStyles = css({
  display: 'block',

  [media.below.sm]: {
    display: 'none',
  },
});

const mobileOnlyStyles = css({
  display: 'none',

  [media.below.sm]: {
    display: 'block',
  },
});
